import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { handleAsyncError, logError } from '../utils/errorHandler';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionTimeoutRef = useRef(null);

  // Session timeout duration (30 minutes of inactivity)
  const SESSION_TIMEOUT = 30 * 60 * 1000;

  // Reset session timeout on user activity
  const resetSessionTimeout = useCallback(() => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }

    const timeout = setTimeout(() => {
      logout();
      alert('Your session has expired due to inactivity. Please log in again.');
    }, SESSION_TIMEOUT);

    sessionTimeoutRef.current = timeout;
  }, [SESSION_TIMEOUT]);

  // Add activity listeners
  useEffect(() => {
    if (currentUser) {
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
      events.forEach(event => {
        document.addEventListener(event, resetSessionTimeout);
      });

      resetSessionTimeout();

      return () => {
        events.forEach(event => {
          document.removeEventListener(event, resetSessionTimeout);
        });
        if (sessionTimeoutRef.current) {
          clearTimeout(sessionTimeoutRef.current);
        }
      };
    }
  }, [currentUser, resetSessionTimeout]);

  // Sign up with email and password
  const signup = async (email, password, displayName) => {
    const result = await handleAsyncError(async () => {
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        
        // Update profile with display name
        if (displayName) {
          await updateProfile(userCredential.user, { displayName });
        }

        // Send email verification
        await sendEmailVerification(userCredential.user);
        
        return userCredential.user;
      } catch (error) {
        // Handle IndexedDB transaction errors
        if (error.message && error.message.includes('transaction was aborted')) {
          console.error('IndexedDB error detected during signup');
          try {
            localStorage.clear();
            sessionStorage.clear();
          } catch (storageError) {
            console.error('Failed to clear storage:', storageError);
          }
          throw new Error('Authentication storage error. Please clear your browser cache and try again.');
        }
        throw error;
      }
    }, { action: 'signup', email });

    return result;
  };

  // Sign in with email and password
  const login = async (email, password) => {
    const result = await handleAsyncError(async () => {
      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        return userCredential.user;
      } catch (error) {
        // Handle IndexedDB transaction errors
        if (error.message && error.message.includes('transaction was aborted')) {
          console.error('IndexedDB error detected, clearing local storage...');
          // Clear potentially corrupted local storage
          try {
            localStorage.clear();
            sessionStorage.clear();
          } catch (storageError) {
            console.error('Failed to clear storage:', storageError);
          }
          throw new Error('Authentication storage error. Please clear your browser cache and try again.');
        }
        throw error;
      }
    }, { action: 'login', email });

    return result;
  };

  // Sign in with Google
  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const result = await handleAsyncError(async () => {
      const userCredential = await signInWithPopup(auth, provider);
      return userCredential.user;
    }, { action: 'loginWithGoogle' });

    return result;
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
        sessionTimeoutRef.current = null;
      }
    } catch (error) {
      logError(error, { action: 'logout' });
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    const result = await handleAsyncError(async () => {
      await sendPasswordResetEmail(auth, email);
      return true;
    }, { action: 'resetPassword', email });

    return result;
  };

  // Update user profile
  const updateUserProfile = async (updates) => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    const result = await handleAsyncError(async () => {
      const firebaseUpdates = {};
      const firestoreUpdates = {};

      Object.keys(updates).forEach(key => {
        if (['displayName', 'photoURL'].includes(key)) {
          firebaseUpdates[key] = updates[key];
        } else {
          firestoreUpdates[key] = updates[key];
        }
      });

      if (Object.keys(firebaseUpdates).length > 0) {
        await updateProfile(currentUser, firebaseUpdates);
      }

      if (Object.keys(firestoreUpdates).length > 0) {
        const userRef = doc(db, 'users', currentUser.uid);
        await setDoc(userRef, firestoreUpdates, { merge: true });
      }

      if (Object.keys(firebaseUpdates).length > 0 || Object.keys(firestoreUpdates).length > 0) {
        const userRef = doc(db, 'users', currentUser.uid);
        const snapshot = await getDoc(userRef);
        setCurrentUser({
          ...currentUser,
          ...firebaseUpdates,
          ...snapshot.data()
        });
      }

      return true;
    }, { action: 'updateProfile', userId: currentUser.uid });

    return result;
  };

  // Resend email verification
  const resendVerificationEmail = async () => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    const result = await handleAsyncError(async () => {
      await sendEmailVerification(currentUser);
      return true;
    }, { action: 'resendVerification', userId: currentUser.uid });

    return result;
  };

  // Get ID token (for API calls)
  const getIdToken = async () => {
    if (!currentUser) {
      return null;
    }

    try {
      const token = await currentUser.getIdToken();
      return token;
    } catch (error) {
      logError(error, { action: 'getIdToken' });
      return null;
    }
  };

  // Refresh ID token
  const refreshToken = async () => {
    if (!currentUser) {
      return null;
    }

    try {
      const token = await currentUser.getIdToken(true);
      return token;
    } catch (error) {
      logError(error, { action: 'refreshToken' });
      return null;
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userRef = doc(db, 'users', user.uid);
          const snapshot = await getDoc(userRef);
          const firestoreData = snapshot.data() || {};
          setCurrentUser({
            ...user,
            ...firestoreData
          });
        } catch (error) {
          logError(error, { action: 'fetchUserProfile' });
          setCurrentUser(user);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    }, (error) => {
      logError(error, { action: 'onAuthStateChanged' });
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    loading,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    updateUserProfile,
    resendVerificationEmail,
    getIdToken,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
